import { cn } from '@veraclins-dev/utils';

import { useIcon } from '../icons';
import { type IconName } from '../icons/name';

import { ComposedTooltip } from './tooltip';
import { Typography } from './typography';

/**
 * Size class names for different icon sizes.
 * Maps size keys to their corresponding Tailwind CSS classes.
 */
const sizeClassName = {
  font: 'size-[1em]',
  xs: 'size-4',
  sm: 'size-5',
  md: 'size-6',
  lg: 'size-8',
  xl: 'size-10',
} as const;

/** Available size options for icons */
type Size = keyof typeof sizeClassName;

/**
 * Gap class names for different icon sizes when used with children.
 * Maps size keys to their corresponding Tailwind CSS gap classes.
 */
const childrenSizeClassName = {
  font: 'gap-1.5',
  xs: 'gap-1.5',
  sm: 'gap-1.5',
  md: 'gap-2',
  lg: 'gap-2',
  xl: 'gap-3',
} satisfies Record<Size, string>;

/**
 * Props for the Icon component.
 * Extends SVG props with additional icon-specific properties.
 */
export type IconProps = React.SVGProps<SVGSVGElement> & {
  /** Name of the icon to display */
  name: IconName;
  /** Size of the icon */
  size?: Size;
  /** Optional tooltip text to show on hover */
  tooltip?: string;
  /** Path to the sprite sheet containing the icon */
  href?: string;
};

/**
 * Internal component that handles the actual icon rendering.
 * Supports both standalone icons and icons with children.
 *
 * @param props - Props for the icon component
 * @returns An SVG element with the specified icon
 */
const Component = ({
  name,
  size = 'font',
  className,
  children,
  href,
  ...props
}: Omit<IconProps, 'tooltip'>) => {
  if (children) {
    return (
      <Typography
        component="span"
        className={cn('inline-flex items-center', childrenSizeClassName[size])}
      >
        <Component
          name={name}
          size={size}
          className={className}
          href={href}
          {...props}
        />
        {children}
      </Typography>
    );
  }
  // An icon is decoration unless the caller names it. Without this, the sprite
  // token leaks into the accessible name of any control wrapping the icon —
  // `<Button leadingIcon="paper-plane">Send</Button>` announced as
  // "paper-plane Send". A caller-supplied `aria-label` / `aria-labelledby`
  // (or `aria-hidden`) is spread after these defaults and wins.
  const labelled = Boolean(props['aria-label'] ?? props['aria-labelledby']);
  return (
    <svg
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      {...props}
      data-slot="icon"
      className={cn(sizeClassName[size], 'inline self-center', className)}
    >
      <use href={`${href}#${name}`} />
    </svg>
  );
};

/**
 * Icon component that renders an SVG icon from a sprite sheet.
 * Supports different sizes, tooltips, and can be used with or without text.
 *
 * Icons are decorative by default (`aria-hidden`), so an icon sitting next to a
 * label never pollutes the accessible name of the control around it. Pass
 * `aria-label` (or `aria-labelledby`, or a `tooltip`) when the icon itself is
 * the only thing conveying meaning — it then renders as `role="img"`.
 *
 * A 1.5 outline stroke follows the weight of the nearest font-weight class
 * (1.5 regular, 2 medium or semibold, 2.5 bold); override it with
 * `className="[--icon-stroke-width:N]"` on the icon or an ancestor.
 *
 * @example
 * ```tsx
 * <Icon name="check" />
 * <Icon name="check" aria-label="Verified" />
 * ```
 *
 * @param props - Props for the Icon component
 * @returns An SVG icon with optional tooltip and text
 * @category icons
 */
function Icon({ tooltip, ...props }: IconProps) {
  const { sprite } = useIcon();

  return tooltip ? (
    <ComposedTooltip
      Trigger={Component}
      // The icon is the tooltip trigger here, so it is content rather than
      // decoration and needs a name of its own; the tooltip text is it.
      TriggerProps={{ 'aria-label': tooltip, ...props, href: sprite }}
      content={tooltip}
    />
  ) : (
    <Component {...props} href={sprite} />
  );
}

export { Icon };
